import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  FileText,
  ScrollText,
  ClipboardList,
  Mail,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
  History,
  Clock,
  ArrowLeft,
  ChevronRight,
  ChevronDown,
  Download,
  Settings,
  Search,
  X,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { jsPDF } from 'jspdf';
import TermsContent, { TERMS_CATEGORIES_COUNT, getTermsAsText } from '../components/shared/TermsContent';
import AdminLayout from '../components/layout/AdminLayout';
import SupervisorLayout from '../components/layout/SupervisorLayout';
import EmployeeLayout from '../components/layout/EmployeeLayout';
import OnboardingBanner from '../components/OnboardingBanner';
import { useOnboarding } from '../contexts/OnboardingContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sanitize HTML for safe rendering (allows tables, links, lists, headings).
const sanitize = (html) => DOMPurify.sanitize(html || '', { ADD_ATTR: ['target', 'rel'] });
// Strip HTML tags for plain-text PDF export.
const htmlToText = (html) => String(html || '')
  .replace(/<\/(p|h\d|li|tr|div|br)>/gi, '\n')
  .replace(/<li[^>]*>/gi, '• ')
  .replace(/<[^>]+>/g, '')
  .replace(/&nbsp;/g, ' ')
  .replace(/&amp;/g, '&')
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .replace(/&quot;/g, '"')
  .replace(/&#39;/g, "'")
  .replace(/\n{3,}/g, '\n\n')
  .trim();

/**
 * Render a long string into a paginated A4 PDF and trigger download.
 *
 * Why we build it ourselves rather than calling a server endpoint: the
 * policy/procedure content lives plain in the DB and there's no server-side
 * PDF renderer for it. Doing it client-side with jsPDF keeps the change
 * additive (no backend churn, no new dependency on a headless browser).
 */
function downloadAsPdf({ title, subtitle, content }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 50;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usableWidth = pageWidth - margin * 2;
  let y = margin;

  // Header band
  doc.setFillColor(24, 24, 27);
  doc.rect(0, 0, pageWidth, 60, 'F');
  doc.setTextColor(250, 204, 21);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('SAN SECURITY', margin, 38);

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(16);
  y = 90;
  const titleLines = doc.splitTextToSize(title || '', usableWidth);
  doc.text(titleLines, margin, y);
  y += titleLines.length * 22 + 6;

  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text(subtitle, margin, y);
    y += 18;
    doc.setTextColor(0, 0, 0);
  }

  // Body
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  const paragraphs = String(content || '').split(/\n\n+/);
  const lineHeight = 15;
  for (const para of paragraphs) {
    const wrapped = doc.splitTextToSize(para.replace(/\n/g, ' '), usableWidth);
    for (const line of wrapped) {
      if (y > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += lineHeight;
    }
    y += 6; // paragraph break
  }

  // Footer on every page
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(
      `Generated ${format(new Date(), 'dd MMM yyyy HH:mm')}  ·  San Security  ·  Page ${i} of ${pageCount}`,
      margin,
      pageHeight - 24,
    );
  }

  const safe = (title || 'document').replace(/[^a-z0-9_-]+/gi, '_').slice(0, 80);
  doc.save(`${safe}.pdf`);
}

const SECTIONS = [
  {
    key: 'terms',
    label: 'Terms & Conditions',
    description: 'The terms that govern your engagement with S.A.N. Security Ltd.',
    icon: FileText,
    accent: 'border-blue-500/40 hover:border-blue-500 bg-blue-500/5',
    iconBg: 'bg-blue-500/10 text-blue-300',
  },
  {
    key: 'policy',
    label: 'Company Policies',
    description: 'All company policies you must read and acknowledge.',
    icon: ScrollText,
    accent: 'border-amber-500/40 hover:border-amber-500 bg-amber-500/5',
    iconBg: 'bg-amber-500/10 text-amber-300',
  },
  {
    key: 'procedures',
    label: 'Company Procedures',
    description: 'Operational procedures and how-to guides for daily work.',
    icon: ClipboardList,
    accent: 'border-green-500/40 hover:border-green-500 bg-green-500/5',
    iconBg: 'bg-green-500/10 text-green-300',
  },
];

/**
 * Unified Legal & Policies page. Route-driven so the user can deep-link.
 *   /{role}/legal              → hub with 3 large cards
 *   /{role}/legal/policy       → Company Policy section only
 *   /{role}/legal/terms        → Terms & Conditions section only
 *   /{role}/legal/procedures   → Procedures section only
 */

// -------------------- Keyword search across all 3 categories --------------------
const CATEGORY_LABELS = { terms: 'Terms & Conditions', policy: 'Company Policies', procedures: 'Company Procedures' };
const CATEGORY_COLORS = { terms: 'text-blue-300', policy: 'text-amber-300', procedures: 'text-green-300' };

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const Highlighted = ({ text, term }) => {
  if (!term) return <>{text}</>;
  const parts = String(text || '').split(new RegExp(`(${escapeRegex(term)})`, 'ig'));
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === term.toLowerCase()
          ? <mark key={i} className="bg-yellow-400 text-black rounded-[2px] px-0.5">{p}</mark>
          : p
      )}
    </>
  );
};

function LegalSearchBar({ basePath, navigate }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults(null); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/legal/search`, { params: { q: term } });
        setResults(res.data.results || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const term = q.trim();
  const grouped = (results || []).reduce((acc, r) => {
    (acc[r.category] = acc[r.category] || []).push(r);
    return acc;
  }, {});

  return (
    <div className="space-y-2" data-testid="legal-search">
      <div className="relative">
        <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search terms, policies & procedures… (e.g. fee apply, shift, uniform)"
          className="w-full h-11 pl-10 pr-10 rounded-sm bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60"
          data-testid="legal-search-input"
        />
        {q && (
          <button
            type="button"
            onClick={() => setQ('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            data-testid="legal-search-clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {term.length >= 2 && (
        <Card className="bg-card border-primary/30 rounded-sm" data-testid="legal-search-results">
          <CardContent className="p-3 sm:p-4 space-y-3 max-h-[28rem] overflow-y-auto">
            {searching ? (
              <p className="text-xs text-muted-foreground italic flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…
              </p>
            ) : results && results.length === 0 ? (
              <p className="text-xs text-muted-foreground italic" data-testid="legal-search-empty">
                No results for “{term}” in Terms, Policies or Procedures.
              </p>
            ) : (
              Object.entries(grouped).map(([cat, rows]) => (
                <div key={cat} className="space-y-1.5">
                  <p className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${CATEGORY_COLORS[cat] || 'text-muted-foreground'}`}>
                    {CATEGORY_LABELS[cat] || cat} · {rows.length} result{rows.length === 1 ? '' : 's'}
                  </p>
                  {rows.map((r) => (
                    <button
                      key={`${cat}-${r.id}`}
                      type="button"
                      onClick={() => navigate(`${basePath}/${cat}?doc=${r.id}`)}
                      className="w-full text-left rounded-sm border border-border/60 hover:border-primary/60 hover:bg-accent/30 transition-colors p-2.5"
                      data-testid={`legal-search-result-${r.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-foreground truncate">
                          <Highlighted text={r.title} term={term} />
                        </span>
                        <Badge variant="outline" className="text-[10px] flex-shrink-0">
                          {r.match_count} match{r.match_count === 1 ? '' : 'es'}
                        </Badge>
                      </div>
                      {r.snippet && (
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                          <Highlighted text={r.snippet} term={term} />
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function LegalAndPoliciesPage({ role }) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const sectionKey = params.section || null;
  const section = SECTIONS.find((s) => s.key === sectionKey) || null;

  const isAdmin = role === 'admin';
  const isSupervisor = role === 'supervisor';
  const canNotify = isAdmin;

  const Layout = isAdmin ? AdminLayout : isSupervisor ? SupervisorLayout : EmployeeLayout;
  const basePath = `/${role}/legal`;

  // ---- Hub (no sub-section selected) ----
  if (!section) {
    return <LegalHub role={role} basePath={basePath} navigate={navigate} Layout={Layout} />;
  }

  // ---- A specific section is selected ----
  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
        <OnboardingBanner />
        <div className="flex items-center justify-between gap-2 border-b border-border pb-4">
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => navigate(basePath)}
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2"
              data-testid="legal-back-btn"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Legal &amp; Policies
            </button>
            <h1 className="text-xl sm:text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
              <section.icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" strokeWidth={1.5} />
              {section.label.toUpperCase()}
            </h1>
          </div>
        </div>

        {section.key === 'policy' && (
          <PolicyOrProcedureSection canNotify={canNotify} kind="policy" />
        )}
        {section.key === 'terms' && (
          <TermsSection canNotify={canNotify} />
        )}
        {section.key === 'procedures' && (
          <PolicyOrProcedureSection canNotify={canNotify} kind="procedure" />
        )}
      </div>
    </Layout>
  );
}

// -------------------- Terms & Conditions section --------------------
// Backed by the `terms_documents` collection. Employees/Supervisors see only
// published items (filtered server-side). Each doc renders its sanitised
// HTML content. The legacy static `TermsContent` is kept as a fallback
// shown only when the DB collection is genuinely empty — useful for
// installs that haven't run the auto-seed.
function TermsSection({ canNotify }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [error, setError] = useState(false);
  const location = useLocation();
  const focusDoc = new URLSearchParams(location.search).get('doc');
  const { checks, required, complete, acceptTerms } = useOnboarding();
  const [accepting, setAccepting] = useState(false);
  // Only render the Accept banner while the gate is active AND terms not yet
  // accepted. Grandfathered active employees never see this.
  const showAcceptBox = !!required && !complete && checks && !checks.terms?.complete;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/terms`);
        if (!cancelled) setItems(res.data || []);
      } catch (e) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Deep-link from search: ?doc={id} → expand + scroll to that document.
  useEffect(() => {
    if (focusDoc && items.some((t) => t.id === focusDoc)) {
      setExpandedId(focusDoc);
      setTimeout(() => document.getElementById(`legal-doc-${focusDoc}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    }
  }, [focusDoc, items]);

  // Accept-Terms panel — shown only during onboarding, above whichever
  // rendering path (static fallback OR live items) below.
  const acceptBanner = showAcceptBox ? (
    <Card
      className="bg-amber-500/10 border-amber-500/40 rounded-sm mb-4"
      data-testid="terms-accept-banner"
    >
      <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-500 uppercase tracking-wider">
            Accept the Company Terms &amp; Conditions
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            After reading the Terms below, click Accept to record your
            acknowledgement. This is one of the four steps that unlock the
            rest of the app.
          </p>
        </div>
        <Button
          size="sm"
          disabled={accepting}
          onClick={async () => {
            setAccepting(true);
            try {
              await acceptTerms();
              toast.success('Company Terms & Conditions accepted');
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Could not record acceptance');
            } finally {
              setAccepting(false);
            }
          }}
          className="bg-amber-500 hover:bg-amber-400 text-black rounded-sm uppercase tracking-wider text-xs h-9"
          data-testid="accept-terms-btn"
        >
          {accepting ? (
            <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />Recording…</>
          ) : (
            <><CheckCircle2 className="w-3.5 h-3.5 mr-2" />I Accept</>
          )}
        </Button>
      </CardContent>
    </Card>
  ) : null;

  if (loading) {
    return (
      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-6 text-sm text-muted-foreground italic">Loading…</CardContent>
      </Card>
    );
  }

  // Fallback: empty collection or load error → show the legacy static content.
  if (error || items.length === 0) {
    return (
      <>
        {acceptBanner}
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-6">
            {canNotify && (
              <div className="flex justify-end mb-4">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadAsPdf({
                    title: 'Terms & Conditions',
                    subtitle: `San Security — Staff Operating Terms · ${TERMS_CATEGORIES_COUNT} categories`,
                    content: getTermsAsText(),
                  })}
                  className="text-xs h-7"
                  data-testid="download-terms-pdf"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Download PDF
                </Button>
              </div>
            )}
            <TermsContent />
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <div className="space-y-3">
      {acceptBanner}
      {items.map((t) => {
        const isOpen = expandedId === t.id;
        return (
          <Card key={t.id} id={`legal-doc-${t.id}`} className="bg-card border-border rounded-sm overflow-hidden" data-testid={`terms-card-${t.id}`}>
            <button
              type="button"
              onClick={() => setExpandedId(isOpen ? null : t.id)}
              className="w-full text-left p-4 flex items-center gap-3 hover:bg-accent/30 transition-colors"
              data-testid={`terms-toggle-${t.id}`}
            >
              <div className="flex-shrink-0">
                {isOpen
                  ? <ChevronDown className="w-4 h-4 text-primary" />
                  : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm sm:text-base font-semibold text-foreground truncate">{t.title}</h3>
                <p className="text-[10px] text-muted-foreground">
                  {t.updated_at ? `Updated ${format(parseISO(t.updated_at), 'dd MMM yyyy')}` : ''}
                </p>
              </div>
            </button>
            {isOpen && (
              <CardContent className="p-5 pt-0 border-t border-border space-y-3">
                {canNotify && (
                  <div className="flex justify-end gap-2 pt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadAsPdf({
                        title: t.title,
                        subtitle: `Terms & Conditions${t.updated_at ? ' · Updated ' + format(parseISO(t.updated_at), 'dd MMM yyyy') : ''}`,
                        content: htmlToText(t.content || ''),
                      })}
                      className="text-xs h-7"
                      data-testid={`terms-download-${t.id}`}
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Download PDF
                    </Button>
                  </div>
                )}
                <div
                  className="prose prose-invert max-w-none text-sm leading-relaxed text-foreground/90 [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:p-2 [&_th]:border [&_th]:border-border [&_th]:p-2 [&_a]:text-primary [&_a]:underline"
                  dangerouslySetInnerHTML={{ __html: sanitize(t.content || '') }}
                />
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// -------------------- Section renderer (Policy OR Procedure) --------------------
// Same UX for both — only the API endpoints + labels differ. Keeps the code
// DRY so adding more procedure-style sections later is one-line.
function PolicyOrProcedureSection({ canNotify, kind }) {
  const isProcedure = kind === 'procedure';
  const labels = isProcedure
    ? { entityListUrl: '/procedures', ackUrl: '/procedures/acknowledge', notifyBase: '/procedures', logUrl: '/procedures/notifications/log', idField: 'procedure_id', emptyTxt: 'No company procedures have been published yet.', ackBtn: 'I have read and understand this procedure', noun: 'procedure' }
    : { entityListUrl: '/policies', ackUrl: '/policies/acknowledge', notifyBase: '/policies', logUrl: '/policies/notifications/log', idField: 'policy_id', emptyTxt: 'No company policies have been published yet.', ackBtn: 'I have read and understand this policy', noun: 'policy' };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acknowledging, setAcknowledging] = useState(null);
  const [notifyingId, setNotifyingId] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const location = useLocation();
  const focusDoc = new URLSearchParams(location.search).get('doc');

  useEffect(() => { fetchItems(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [kind]);

  // Deep-link from search: ?doc={id} → expand + scroll to that document.
  useEffect(() => {
    if (focusDoc && items.some((p) => p.id === focusDoc)) {
      setExpandedId(focusDoc);
      setTimeout(() => document.getElementById(`legal-doc-${focusDoc}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    }
  }, [focusDoc, items]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}${labels.entityListUrl}`);
      setItems(res.data || []);
    } catch (e) {
      toast.error(`Could not load ${labels.noun}s`);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (id) => {
    setAcknowledging(id);
    try {
      await axios.post(`${API}${labels.ackUrl}`, { [labels.idField]: id });
      toast.success(`${labels.noun.charAt(0).toUpperCase() + labels.noun.slice(1)} acknowledged`);
      await fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not acknowledge');
    } finally {
      setAcknowledging(null);
    }
  };

  const handleNotify = async (entity) => {
    if (!window.confirm(`Send "${entity.title}" to every active staff member via email?\n\nA full audit log entry will be saved.`)) return;
    setNotifyingId(entity.id);
    try {
      const res = await axios.post(`${API}${labels.notifyBase}/${entity.id}/notify`);
      const { emails_sent, emails_failed, recipients_total } = res.data;
      if (emails_failed > 0) {
        toast.warning(`Sent ${emails_sent}/${recipients_total} · ${emails_failed} failed (logged)`);
      } else {
        toast.success(`Email sent to ${emails_sent} staff member${emails_sent === 1 ? '' : 's'}`);
      }
      if (showLog) await loadLog();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send notifications');
    } finally {
      setNotifyingId(null);
    }
  };

  const loadLog = async () => {
    setLogLoading(true);
    try {
      const res = await axios.get(`${API}${labels.logUrl}`);
      setLog(res.data.items || []);
    } catch (e) {
      toast.error('Could not load notification log');
    } finally {
      setLogLoading(false);
    }
  };

  const titleKey = isProcedure ? 'procedure_title' : 'policy_title';

  return (
    <div className="space-y-3">
      {canNotify && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={async () => { setShowLog(v => !v); if (!showLog) await loadLog(); }}
            data-testid="toggle-notification-log"
          >
            <History className="w-3.5 h-3.5 mr-1.5" />
            {showLog ? 'Hide notification log' : 'View notification log'}
          </Button>
        </div>
      )}

      {showLog && canNotify && (
        <Card className="bg-card border-primary/30 rounded-sm">
          <CardContent className="p-4 space-y-2">
            <div className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Mail className="w-4 h-4 text-primary" />
              Notification audit log
            </div>
            {logLoading ? (
              <p className="text-xs text-muted-foreground italic">Loading…</p>
            ) : log.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No notifications sent yet.</p>
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {log.map((l) => (
                  <div key={l.id} className="text-xs border border-border/40 rounded-sm p-2 bg-background" data-testid={`log-${l.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <strong className="text-foreground">{l[titleKey] || l.policy_title || l.procedure_title}</strong>
                      <Badge className={`text-[10px] ${l.status === 'sent' ? 'bg-green-600 hover:bg-green-600' : l.status === 'partial' ? 'bg-orange-600 hover:bg-orange-600' : 'bg-red-600 hover:bg-red-600'}`}>
                        {l.status === 'sent' ? 'SENT' : l.status === 'partial' ? 'PARTIAL' : 'FAILED'}
                      </Badge>
                    </div>
                    <div className="text-muted-foreground mt-1">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {format(parseISO(l.sent_at), 'dd MMM yyyy HH:mm')} by {l.sent_by_name || 'admin'} · {l.emails_sent}/{l.recipients_total} sent
                      {l.emails_failed > 0 && <> · <span className="text-red-400">{l.emails_failed} failed</span></>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground italic">Loading…</p>
      ) : items.length === 0 ? (
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-6 text-center text-sm text-muted-foreground italic">
            {labels.emptyTxt}
          </CardContent>
        </Card>
      ) : items.map((p) => {
        const isOpen = expandedId === p.id;
        return (
        <Card key={p.id} id={`legal-doc-${p.id}`} className="bg-card border-border rounded-sm overflow-hidden" data-testid={`${labels.noun}-card-${p.id}`}>
          {/* Compact list row — click to toggle */}
          <button
            type="button"
            onClick={() => setExpandedId(isOpen ? null : p.id)}
            className="w-full text-left p-4 flex items-center gap-3 hover:bg-accent/30 transition-colors"
            data-testid={`${labels.noun}-toggle-${p.id}`}
          >
            <div className="flex-shrink-0">
              {isOpen
                ? <ChevronDown className="w-4 h-4 text-primary" />
                : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm sm:text-base font-semibold text-foreground truncate">{p.title}</h3>
              <p className="text-[10px] text-muted-foreground">
                Issued {format(parseISO(p.created_at), 'dd MMM yyyy')}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {p.acknowledged ? (
                <Badge className="text-[10px] bg-green-600 hover:bg-green-600">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Acknowledged
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] border-orange-500/40 text-orange-300">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Pending
                </Badge>
              )}
            </div>
          </button>

          {/* Expanded content + actions */}
          {isOpen && (
            <CardContent className="p-5 pt-0 border-t border-border space-y-3">
              {canNotify && (
                <div className="flex justify-end gap-2 pt-3 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadAsPdf({
                        title: p.title,
                        subtitle: `${isProcedure ? 'Company Procedure' : 'Company Policy'}${p.updated_at ? ' · Updated ' + format(parseISO(p.updated_at), 'dd MMM yyyy') : ''}`,
                        content: htmlToText(p.content || ''),
                      });
                    }}
                    className="text-xs h-7"
                    data-testid={`download-pdf-${p.id}`}
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Download PDF
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => { e.stopPropagation(); handleNotify(p); }}
                    disabled={notifyingId === p.id}
                    className="text-xs h-7"
                    data-testid={`notify-${p.id}`}
                  >
                    {notifyingId === p.id
                      ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      : <Send className="w-3 h-3 mr-1" />}
                    Notify staff
                  </Button>
                </div>
              )}
              <div
                className="prose prose-invert max-w-none text-sm leading-relaxed text-foreground/90 [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:p-2 [&_th]:border [&_th]:border-border [&_th]:p-2 [&_a]:text-primary [&_a]:underline whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: sanitize(p.content || '') }}
              />
              {p.acknowledged ? (
                <p className="text-xs text-green-400 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Acknowledged {p.acknowledged_at ? format(parseISO(p.acknowledged_at), 'dd MMM yyyy HH:mm') : ''}
                </p>
              ) : (
                <Button
                  onClick={(e) => { e.stopPropagation(); handleAcknowledge(p.id); }}
                  disabled={acknowledging === p.id}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid={`acknowledge-${p.id}`}
                >
                  {acknowledging === p.id
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  {labels.ackBtn}
                </Button>
              )}
            </CardContent>
          )}
        </Card>
        );
      })}
    </div>
  );
}

// Keep the legacy export name so any older imports keep working
function PolicySection({ canNotify }) {
  return <PolicyOrProcedureSection canNotify={canNotify} kind="policy" />;
}

// -------------------- Hub component --------------------
// Shown when no specific section is selected. Pulls live counts for
// Policies and Procedures and — for employees only — derives a personal
// compliance summary (total required docs vs how many are still pending
// the logged-in user's acknowledgement). Terms & Conditions is a static
// structured document, so its count comes from the bundled categories
// constant `TERMS_CATEGORIES_COUNT`.
function LegalHub({ role, basePath, navigate, Layout }) {
  const isEmployee = role === 'employee';
  const isAdmin = role === 'admin';
  const [counts, setCounts] = useState({ terms: TERMS_CATEGORIES_COUNT, policy: 0, procedures: 0 });
  // Personal pending count (employee only) — every un-acknowledged
  // policy/procedure adds 1. Terms is treated as a single required read,
  // but we don't persist its acknowledgement separately so it's excluded
  // from the pending tally to avoid a "phantom" pending forever.
  const [pendingTotal, setPendingTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pol, proc, terms] = await Promise.all([
          axios.get(`${API}/policies`),
          axios.get(`${API}/procedures`),
          axios.get(`${API}/terms`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const policies = pol.data || [];
        const procedures = proc.data || [];
        const termsList = terms.data || [];
        setCounts({
          terms: termsList.length || TERMS_CATEGORIES_COUNT,
          policy: policies.length,
          procedures: procedures.length,
        });
        if (isEmployee) {
          // Backend already returns `acknowledged: bool` scoped to the
          // current user, so we only count what THIS employee still owes.
          const pendingPol = policies.filter((p) => !p.acknowledged).length;
          const pendingProc = procedures.filter((p) => !p.acknowledged).length;
          setPendingTotal(pendingPol + pendingProc);
        }
      } catch {
        /* counts default to TERMS_CATEGORIES_COUNT / 0 — no toast: this is
           a passive summary, the user can still navigate into each section. */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isEmployee]);

  const totalDocs = counts.terms + counts.policy + counts.procedures;

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
        <OnboardingBanner />
        <div className="border-b border-border pb-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl sm:text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-primary" strokeWidth={1.5} />
              LEGAL &amp; POLICIES
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              Review and acknowledge all required company documents.
            </p>
          </div>
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => navigate('/admin/legal/manage')}
              className="border-primary/40 text-primary hover:bg-primary/10 rounded-sm"
              data-testid="legal-manage-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Manage documents
            </Button>
          )}
        </div>

        {/* Personal compliance summary — employees only. Source: each item's
            `acknowledged` flag returned by /policies and /procedures, scoped
            server-side to the logged-in user. No other employees' data is
            ever exposed here. */}
        {isEmployee && !loading && (
          <Card
            className={`rounded-sm border ${pendingTotal > 0
              ? 'border-orange-500/40 bg-orange-500/5'
              : 'border-green-500/40 bg-green-500/5'}`}
            data-testid="legal-compliance-summary"
          >
            <CardContent className="p-4 sm:p-5">
              <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                Legal Compliance
              </div>
              <div className="text-2xl font-semibold text-foreground mt-0.5">
                {totalDocs} documents
              </div>
              {pendingTotal > 0 ? (
                <p
                  className="text-sm text-orange-300 mt-2 flex items-center gap-2"
                  data-testid="legal-compliance-pending"
                >
                  <AlertCircle className="w-4 h-4" />
                  {pendingTotal} document{pendingTotal === 1 ? '' : 's'} still require{pendingTotal === 1 ? 's' : ''} your acknowledgement
                </p>
              ) : (
                <p
                  className="text-sm text-green-300 mt-2 flex items-center gap-2"
                  data-testid="legal-compliance-ok"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  All required documents acknowledged
                </p>
              )}
            </CardContent>
          </Card>
        )}

        <LegalSearchBar basePath={basePath} navigate={navigate} />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="legal-hub">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const count = counts[s.key] ?? 0;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => navigate(`${basePath}/${s.key}`)}
                data-testid={`legal-card-${s.key}`}
                className={`text-left rounded-sm border transition-all p-5 ${s.accent} flex flex-col gap-3 group`}
              >
                <div className={`w-11 h-11 rounded-sm flex items-center justify-center ${s.iconBg}`}>
                  <Icon className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-base font-semibold text-foreground">{s.label}</div>
                  <div
                    className="text-xs text-muted-foreground mt-0.5"
                    data-testid={`legal-count-${s.key}`}
                  >
                    {loading ? '…' : `${count} document${count === 1 ? '' : 's'}`}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{s.description}</div>
                </div>
                <div className="flex items-center text-xs text-primary mt-auto group-hover:gap-2 gap-1 transition-all">
                  Review
                  <ChevronRight className="w-3.5 h-3.5" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
